/**
 * GoodGuardian — App Context
 *
 * Single source of truth for:
 *  - Auth state (user, token)
 *  - All user data (events, comms, documents, chat history, files)
 *
 * On mount: if a token exists in localStorage, validates it via /api/auth/me
 * and loads all user data. On logout, clears everything.
 *
 * All setters perform optimistic local updates + background API sync.
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { getToken, clearToken, data as api, fetchMe } from '@/utils/auth'

const AppContext = createContext(null)

export function AppProvider({ children }) {
  // ── Auth state ───────────────────────────────────────────────────────────────
  const [user, setUser]               = useState(null)
  const [isOnboarded, setIsOnboarded] = useState(false)
  const [authLoading, setAuthLoading] = useState(true)   // true until token validated

  // ── App data ─────────────────────────────────────────────────────────────────
  const [chatHistory, setChatHistory]   = useState([])
  const [cpsHistory,  setCpsHistory]    = useState([])
  const [documents,   setDocuments]     = useState([])
  const [events,      setEvents]        = useState([])
  const [comms,       setComms]         = useState([])
  const [files,       setFiles]         = useState([])

  // ── Detected location (GPS or IP) ────────────────────────────────────────────
  const [detectedLocation, setDetectedLocation] = useState(() => {
    try { return JSON.parse(localStorage.getItem('cg_location') || 'null') } catch { return null }
  })

  // ── Co-parenting data ─────────────────────────────────────────────────────────
  const [kidProfiles,       setChildren]          = useState([])
  const [calendarEvents,    setCalendarEvents]    = useState([])
  const [calendarRequests,  setCalendarRequests]  = useState([])
  const [orders,            setOrders]            = useState([])
  const [notifications,  setNotifications]  = useState([])
  const [unreadCount,    setUnreadCount]     = useState(0)
  const [coParent,       setCoParent]        = useState(null)   // primary active link (backward compat)
  const [coParents,      setCoParents]       = useState([])     // all active co-parent links
  const [messages,       setMessages]        = useState([])
  const [messageCount,   setMessageCount]    = useState(0)
  const [directMsgCount, setDirectMsgCount]  = useState(0) // unread count across all secure conversations
  const [wellbeingLogs,  setWellbeingLogs]   = useState([])
  const [violations,     setViolations]      = useState([])

  // ── Load all user data from API ───────────────────────────────────────────────
  const loadAllData = useCallback(async () => {
    try {
      const [eventsRes, commsRes, docsRes, chatRes, cpsRes, filesRes,
             childrenRes, calRes, calReqRes, ordersRes, notifRes, inviteRes,
             wellbeingRes, violationsRes] = await Promise.allSettled([
        api.getEvents(),
        api.getComms(),
        api.getDocs(),
        api.getChats('chat'),
        api.getChats('cps'),
        api.getFiles(),
        api.getChildren(),
        api.getCalendar(),
        api.getCalRequests(),
        api.getOrders(),
        api.getNotifications(),
        api.getInvites(),
        api.getWellbeing(),
        api.getViolations(),
      ])

      if (eventsRes.status === 'fulfilled')
        setEvents(eventsRes.value.events.map(e => ({ ...e, createdAt: e.created_at })))
      if (commsRes.status === 'fulfilled')
        setComms(commsRes.value.comms.map(e => ({ ...e, createdAt: e.created_at })))
      if (docsRes.status === 'fulfilled') {
        setDocuments(docsRes.value.documents.map(d => ({
          id: d.id, title: d.title, emoji: d.emoji, date: d.created_at?.split('T')[0], status: d.status,
        })))
      }
      if (chatRes.status === 'fulfilled') {
        const msgs = chatRes.value.messages.map(m => ({
          id: m.id, role: m.role, text: m.content, time: new Date(m.created_at),
        }))
        if (msgs.length) {
          // Auto-expire: if the last message is > 24 hours old, silently clear history
          const lastMsg = msgs[msgs.length - 1]
          const ageMs = Date.now() - new Date(lastMsg.time).getTime()
          const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000
          if (ageMs > TWENTY_FOUR_HOURS) {
            // Silently delete stale conversation in background
            api.clearChat('chat').catch(() => {})
          } else {
            setChatHistory(msgs)
          }
        }
      }
      if (cpsRes.status === 'fulfilled') {
        const msgs = cpsRes.value.messages.map(m => ({
          id: m.id, role: m.role, text: m.content, time: new Date(m.created_at),
        }))
        if (msgs.length) setCpsHistory(msgs)
      }
      if (filesRes.status === 'fulfilled') setFiles(filesRes.value.files)
      if (childrenRes.status === 'fulfilled') setChildren(childrenRes.value.children)
      if (calRes.status === 'fulfilled')    setCalendarEvents(calRes.value.events)
      if (calReqRes.status === 'fulfilled') setCalendarRequests(calReqRes.value.requests || [])
      if (ordersRes.status === 'fulfilled') setOrders(ordersRes.value.orders)
      if (notifRes.status === 'fulfilled') {
        setNotifications(notifRes.value.notifications)
        setUnreadCount(notifRes.value.unread)
      }
      if (inviteRes.status === 'fulfilled') {
        const list = inviteRes.value.active_list || (inviteRes.value.active ? [inviteRes.value.active] : [])
        setCoParents(list)
        setCoParent(list[0] || null)
      }
      if (wellbeingRes.status   === 'fulfilled') setWellbeingLogs(wellbeingRes.value.logs || [])
      if (violationsRes.status  === 'fulfilled') setViolations(violationsRes.value.violations || [])

      // Load in-app messages
      try {
        const msgRes = await api.getMessages()
        setMessages(msgRes.messages || [])
        setMessageCount(msgRes.unread || 0)
      } catch {}

      // Load direct-message unread count
      try {
        const convRes = await api.getConversations()
        const total = (convRes.conversations || []).reduce((s, c) => s + (c.unread_count || 0), 0)
        setDirectMsgCount(total)
      } catch {}
    } catch { /* Data load errors are non-fatal */ }
  }, [])

  // ── Load user profile + data from API ────────────────────────────────────────
  const loadUserFromApi = useCallback(async (userOverride) => {
    const userData = userOverride || (getToken() ? await fetchMe().catch(() => null) : null)
    if (!userData) return
    setUser(userData)
    const sit = userData.situation
    setIsOnboarded(!!(userData.state && (Array.isArray(sit) ? sit.length > 0 : sit)))
    await loadAllData()
  }, [loadAllData])

  // ── Bootstrap: validate stored token on app start ────────────────────────────
  useEffect(() => {
    const bootstrap = async () => {
      if (!getToken()) { setAuthLoading(false); return }
      try {
        await loadUserFromApi()
      } catch {
        clearToken()
      } finally {
        setAuthLoading(false)
      }
    }
    bootstrap()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Logout ────────────────────────────────────────────────────────────────────
  const logout = useCallback(() => {
    clearToken()
    setUser(null)
    setIsOnboarded(false)
    setChatHistory([])
    setCpsHistory([])
    setDocuments([])
    setEvents([])
    setComms([])
    setFiles([])
    setChildren([])
    setCalendarEvents([])
    setCalendarRequests([])
    setOrders([])
    setNotifications([])
    setUnreadCount(0)
    setCoParent(null)
    setMessages([])
    setMessageCount(0)
    setWellbeingLogs([])
    setViolations([])
  }, [])

  // ── Poll direct-message unread count every 30 s while logged in ──────────────
  useEffect(() => {
    if (!user) return
    const tick = async () => {
      try {
        const d = await api.getConversations()
        const total = (d.conversations || []).reduce((s, c) => s + (c.unread_count || 0), 0)
        setDirectMsgCount(total)
      } catch {}
    }
    const id = setInterval(tick, 30_000)
    return () => clearInterval(id)
  }, [user])

  // ── Event log actions ─────────────────────────────────────────────────────────
  const addEvent = useCallback(async (eventData) => {
    const tempId = `temp-${Date.now()}`
    const entry  = { ...eventData, id: tempId, createdAt: new Date().toISOString() }
    setEvents(prev => [entry, ...prev])
    try {
      const saved = await api.postEvent(eventData)
      setEvents(prev => prev.map(e => e.id === tempId ? { ...saved, createdAt: saved.createdAt || saved.created_at } : e))
    } catch { setEvents(prev => prev.filter(e => e.id !== tempId)) }
  }, [])

  const updateEvent = useCallback(async (id, fields) => {
    setEvents(prev => prev.map(e => e.id === id ? { ...e, ...fields } : e))
    try {
      const saved = await api.putEvent(id, fields)
      setEvents(prev => prev.map(e => e.id === id ? { ...e, ...saved } : e))
    } catch { /* rollback */ setEvents(prev => prev.map(e => e.id === id ? { ...e } : e)) }
  }, [])

  const deleteEvent = useCallback(async (id) => {
    setEvents(prev => prev.filter(e => e.id !== id))
    api.deleteEvent(id).catch(() => {})
  }, [])

  // ── Comms log actions ─────────────────────────────────────────────────────────
  const addComm = useCallback(async (commData) => {
    const tempId = `temp-${Date.now()}`
    const entry  = { ...commData, id: tempId, createdAt: new Date().toISOString() }
    setComms(prev => [entry, ...prev])
    try {
      const saved = await api.postComm(commData)
      setComms(prev => prev.map(c => c.id === tempId ? { ...saved, createdAt: saved.createdAt || saved.created_at } : c))
    } catch { setComms(prev => prev.filter(c => c.id !== tempId)) }
  }, [])

  const deleteComm = useCallback(async (id) => {
    setComms(prev => prev.filter(c => c.id !== id))
    api.deleteComm(id).catch(() => {})
  }, [])

  const updateComm = useCallback(async (id, updates) => {
    // Optimistic update
    setComms(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c))
    try {
      const saved = await api.putComm(id, updates)
      setComms(prev => prev.map(c => c.id === id
        ? { ...c, ...saved, createdAt: saved.createdAt || saved.created_at || c.createdAt }
        : c
      ))
    } catch {
      // Leave optimistic state in place; the user can retry by saving again
    }
  }, [])

  // ── Document actions ──────────────────────────────────────────────────────────
  const addDocument = useCallback(async (docData) => {
    const tempId = `temp-${Date.now()}`
    const entry  = { ...docData, id: tempId, date: new Date().toLocaleDateString() }
    setDocuments(prev => [entry, ...prev])
    try {
      const saved = await api.postDoc(docData)
      setDocuments(prev => prev.map(d => d.id === tempId ? { ...saved, date: saved.createdAt?.split('T')[0] || entry.date } : d))
    } catch { setDocuments(prev => prev.filter(d => d.id !== tempId)) }
  }, [])

  const updateDocument = useCallback(async (id, updates) => {
    setDocuments(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d))
    try {
      await api.putDoc(id, updates)
    } catch {
      // Revert optimistic update by reloading from API
      try {
        const res = await api.getDocs()
        setDocuments(res.documents.map(d => ({
          id: d.id, title: d.title, emoji: d.emoji, date: d.created_at?.split('T')[0], status: d.status,
        })))
      } catch { /* non-fatal */ }
    }
  }, [])

  const deleteDocument = useCallback(async (id) => {
    setDocuments(prev => prev.filter(d => d.id !== id))
    api.deleteDoc(id).catch(() => {})
  }, [])

  // ── Chat history actions (Legal Chat) ─────────────────────────────────────────
  const appendChatMessage = useCallback(async (msg) => {
    setChatHistory(prev => {
      const updated = [...prev, msg]
      // Sync to API in background (skip disclaimer message)
      if (msg.id !== 'disclaimer' && getToken()) {
        api.postChat({ agent: 'chat', role: msg.role, content: msg.text }).catch(() => {})
      }
      return updated
    })
  }, [])

  // ── Clear legal chat history ──────────────────────────────────────────────────
  const clearChatHistory = useCallback(async () => {
    setChatHistory([])
    if (getToken()) {
      api.clearChat('chat').catch(() => {})
    }
  }, [])

  // ── CPS chat history actions ──────────────────────────────────────────────────
  const appendCpsMessage = useCallback(async (msg) => {
    setCpsHistory(prev => {
      const updated = [...prev, msg]
      if (msg.id !== 'disclaimer' && getToken()) {
        api.postChat({ agent: 'cps', role: msg.role, content: msg.text }).catch(() => {})
      }
      return updated
    })
  }, [])

  // ── File actions ──────────────────────────────────────────────────────────────
  const uploadFile = useCallback(async (file) => {
    const saved = await api.uploadFile(file)
    setFiles(prev => [{ ...saved, filename: saved.filename || file.name }, ...prev])
    return saved
  }, [])

  const renameFile = useCallback(async (id, filename) => {
    const result = await api.renameFile(id, filename)
    setFiles(prev => prev.map(f => f.id === id ? { ...f, filename: result.filename } : f))
    return result
  }, [])

  const deleteFile = useCallback(async (id) => {
    setFiles(prev => prev.filter(f => f.id !== id))
    api.deleteFile(id).catch(() => {})
  }, [])

  // ── Children actions ──────────────────────────────────────────────────────────
  const addChild = useCallback(async (childData) => {
    const tempId = `temp-${Date.now()}`
    setChildren(prev => [{ ...childData, id: tempId }, ...prev])
    try {
      const saved = await api.postChild(childData)
      setChildren(prev => prev.map(c => c.id === tempId ? saved.child : c))
      return saved.child
    } catch { setChildren(prev => prev.filter(c => c.id !== tempId)) }
  }, [])

  const updateChild = useCallback(async (id, data) => {
    setChildren(prev => prev.map(c => c.id === id ? { ...c, ...data } : c))
    api.putChild(id, data).catch(() => {})
  }, [])

  const deleteChild = useCallback(async (id) => {
    setChildren(prev => prev.filter(c => c.id !== id))
    api.deleteChild(id).catch(() => {})
  }, [])

  // ── Calendar actions ──────────────────────────────────────────────────────────
  const addCalendarEvent = useCallback(async (eventData) => {
    const tempId = `temp-${Date.now()}`
    setCalendarEvents(prev => [...prev, { ...eventData, id: tempId, ownership: 'own' }])
    try {
      const saved = await api.postCalEvent(eventData)
      setCalendarEvents(prev => prev.map(e => e.id === tempId ? { ...saved.event, ownership: 'own' } : e))
    } catch { setCalendarEvents(prev => prev.filter(e => e.id !== tempId)) }
  }, [])

  const updateCalendarEvent = useCallback(async (id, data) => {
    setCalendarEvents(prev => prev.map(e => e.id === id ? { ...e, ...data } : e))
    api.putCalEvent(id, data).catch(() => {})
  }, [])

  const deleteCalendarEvent = useCallback(async (id) => {
    setCalendarEvents(prev => prev.filter(e => e.id !== id))
    api.deleteCalEvent(id).catch(() => {})
  }, [])

  const refreshCalendar = useCallback(async () => {
    try {
      const res = await api.getCalendar()
      setCalendarEvents(res.events)
    } catch { /* non-fatal */ }
  }, [])

  // ── Calendar change request actions ──────────────────────────────────────────
  const createCalendarRequest = useCallback(async (eventId, proposed, reason) => {
    const res = await api.postCalRequest({ event_id: eventId, ...proposed, reason })
    setCalendarRequests(prev => [{ ...res.request, ...proposed, reason }, ...prev])
  }, [])

  const approveCalendarRequest = useCallback(async (requestId) => {
    await api.putCalRequest(requestId, { action: 'approve' })
    setCalendarRequests(prev => prev.filter(r => r.id !== requestId))
    // Refresh events so the approved changes appear on the calendar
    try { const res = await api.getCalendar(); setCalendarEvents(res.events) } catch { /* non-fatal */ }
  }, [])

  const rejectCalendarRequest = useCallback(async (requestId) => {
    await api.putCalRequest(requestId, { action: 'reject' })
    setCalendarRequests(prev => prev.filter(r => r.id !== requestId))
  }, [])

  const cancelCalendarRequest = useCallback(async (requestId) => {
    await api.deleteCalRequest(requestId)
    setCalendarRequests(prev => prev.filter(r => r.id !== requestId))
  }, [])

  const refreshCalendarRequests = useCallback(async () => {
    try { const res = await api.getCalRequests(); setCalendarRequests(res.requests || []) } catch { /* non-fatal */ }
  }, [])

  // ── Wellbeing log actions ─────────────────────────────────────────────────────
  const addWellbeingLog = useCallback(async (body) => {
    const tempId = `temp-${Date.now()}`
    setWellbeingLogs(prev => [{ ...body, id: tempId, created_at: new Date().toISOString() }, ...prev])
    try {
      const saved = await api.postWellbeing(body)
      setWellbeingLogs(prev => prev.map(l => l.id === tempId ? saved.log : l))
    } catch {
      setWellbeingLogs(prev => prev.filter(l => l.id !== tempId))
      throw new Error('Failed to save wellbeing log')
    }
  }, [])

  const updateWellbeingLog = useCallback(async (id, body) => {
    setWellbeingLogs(prev => prev.map(l => l.id === id ? { ...l, ...body } : l))
    try {
      const saved = await api.putWellbeing(id, body)
      setWellbeingLogs(prev => prev.map(l => l.id === id ? { ...l, ...saved.log } : l))
    } catch {
      try { const res = await api.getWellbeing(); setWellbeingLogs(res.logs || []) } catch { /* non-fatal */ }
      throw new Error('Failed to update wellbeing log')
    }
  }, [])

  const deleteWellbeingLog = useCallback(async (id) => {
    setWellbeingLogs(prev => prev.filter(l => l.id !== id))
    try { await api.deleteWellbeing(id) } catch {
      // Non-fatal — entry already removed optimistically; reload to restore
    }
  }, [])

  const refreshWellbeingLogs = useCallback(async () => {
    try { const res = await api.getWellbeing(); setWellbeingLogs(res.logs || []) } catch { /* non-fatal */ }
  }, [])

  // ── Violation actions ─────────────────────────────────────────────────────────
  const addViolation = useCallback(async (body) => {
    const tempId = `temp-${Date.now()}`
    setViolations(prev => [{ ...body, id: tempId, created_at: new Date().toISOString() }, ...prev])
    try {
      const saved = await api.postViolation(body)
      setViolations(prev => prev.map(v => v.id === tempId ? saved.violation : v))
      return saved.violation
    } catch {
      setViolations(prev => prev.filter(v => v.id !== tempId))
      throw new Error('Failed to save violation')
    }
  }, [])

  const updateViolation = useCallback(async (id, body) => {
    setViolations(prev => prev.map(v => v.id === id ? { ...v, ...body } : v))
    try {
      const saved = await api.putViolation(id, body)
      setViolations(prev => prev.map(v => v.id === id ? saved.violation : v))
    } catch {
      // Revert on failure — reload from server
      try { const res = await api.getViolations(); setViolations(res.violations || []) } catch { /* non-fatal */ }
      throw new Error('Failed to update violation')
    }
  }, [])

  const deleteViolation = useCallback(async (id) => {
    setViolations(prev => prev.filter(v => v.id !== id))
    try { await api.deleteViolation(id) } catch { /* non-fatal */ }
  }, [])

  const refreshViolations = useCallback(async () => {
    try { const res = await api.getViolations(); setViolations(res.violations || []) } catch { /* non-fatal */ }
  }, [])

  // ── Court order actions ────────────────────────────────────────────────────────
  const addOrder = useCallback(async (orderData) => {
    const tempId = `temp-${Date.now()}`
    setOrders(prev => [{ ...orderData, id: tempId, ownership: 'own', status: 'active' }, ...prev])
    try {
      const saved = await api.postOrder(orderData)
      setOrders(prev => prev.map(o => o.id === tempId ? { ...saved.order, ownership: 'own' } : o))
    } catch { setOrders(prev => prev.filter(o => o.id !== tempId)) }
  }, [])

  const updateOrder = useCallback(async (id, data) => {
    setOrders(prev => prev.map(o => o.id === id ? { ...o, ...data } : o))
    api.putOrder(id, data).catch(() => {})
  }, [])

  const deleteOrder = useCallback(async (id) => {
    setOrders(prev => prev.filter(o => o.id !== id))
    api.deleteOrder(id).catch(() => {})
  }, [])

  // ── Co-parent invite actions ───────────────────────────────────────────────────
  const sendCoParentInvite = useCallback(async (email) => {
    const saved = await api.sendInvite(email)
    return saved.invite
  }, [])

  const acceptCoParentInvite = useCallback(async (token) => {
    await api.acceptInvite(token)
    const inviteRes = await api.getInvites()
    const list = inviteRes.active_list || (inviteRes.active ? [inviteRes.active] : [])
    setCoParents(list)
    setCoParent(list[0] || null)
    // Reload shared data after linking
    const [childrenRes, calRes, ordersRes] = await Promise.allSettled([
      api.getChildren(), api.getCalendar(), api.getOrders(),
    ])
    if (childrenRes.status === 'fulfilled') setChildren(childrenRes.value.children)
    if (calRes.status === 'fulfilled')      setCalendarEvents(calRes.value.events)
    if (ordersRes.status === 'fulfilled')   setOrders(ordersRes.value.orders)
  }, [])

  const revokeCoParentLink = useCallback(async (id) => {
    await api.revokeInvite(id)
    setCoParents(prev => {
      const updated = prev.filter(cp => cp.id !== id)
      setCoParent(updated[0] || null)
      return updated
    })
  }, [])

  // ── Notification actions ───────────────────────────────────────────────────────
  const markNotifRead = useCallback(async (id) => {
    setNotifications(prev => prev.map(n =>
      (id ? n.id === id : true) ? { ...n, read: 1 } : n
    ))
    setUnreadCount(0)
    api.markNotifRead(id).catch(() => {})
  }, [])

  const refreshNotifications = useCallback(async () => {
    try {
      const res = await api.getNotifications()
      setNotifications(res.notifications)
      setUnreadCount(res.unread)
    } catch { /* non-fatal */ }
  }, [])

  const value = {
    // Auth
    user, setUser, isOnboarded, setIsOnboarded, authLoading, loadUserFromApi, logout,

    // Core data
    events,      addEvent,      updateEvent,   deleteEvent,
    comms,       addComm,       updateComm,    deleteComm,
    documents,   addDocument,   updateDocument,   deleteDocument,
    files,       uploadFile,    renameFile,    deleteFile,

    // Chat
    chatHistory,  setChatHistory,  appendChatMessage,  clearChatHistory,
    cpsHistory,   setCpsHistory,   appendCpsMessage,

    // Co-parenting
    children: kidProfiles, addChild, updateChild, deleteChild,
    calendarEvents, addCalendarEvent, updateCalendarEvent, deleteCalendarEvent, refreshCalendar,
    calendarRequests, createCalendarRequest, approveCalendarRequest, rejectCalendarRequest,
    cancelCalendarRequest, refreshCalendarRequests,
    wellbeingLogs, addWellbeingLog, updateWellbeingLog, deleteWellbeingLog, refreshWellbeingLogs,
    violations, addViolation, updateViolation, deleteViolation, refreshViolations,
    orders,         addOrder,       updateOrder,       deleteOrder,
    coParent,       setCoParent,    sendCoParentInvite, acceptCoParentInvite, revokeCoParentLink,
    coParents,      setCoParents,
    notifications,  unreadCount,   markNotifRead,     refreshNotifications,
    messages,       messageCount,  setMessageCount,   setMessages,
    directMsgCount, setDirectMsgCount,

    // Location
    detectedLocation, setDetectedLocation,
  }

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>
}

export function useApp() {
  const ctx = useContext(AppContext)
  if (!ctx) throw new Error('useApp must be used within AppProvider')
  return ctx
}
